import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { GamblingGameProps } from './GamblingHall';
import diceStyles from './DiceGame.module.css';
import styles from './HighLow.module.css';

// ── Dice face helper (reused from DiceGame) ──────────────────────────────────
const FACE_DOTS: Record<number, [number, number][]> = {
  1: [[50, 50]],
  2: [[25, 25], [75, 75]],
  3: [[25, 25], [50, 50], [75, 75]],
  4: [[25, 25], [75, 25], [25, 75], [75, 75]],
  5: [[25, 25], [75, 25], [50, 50], [25, 75], [75, 75]],
  6: [[25, 20], [75, 20], [25, 50], [75, 50], [25, 80], [75, 80]],
};

const DieFace: React.FC<{ value: number; rolling: boolean; settled: boolean }> = ({ value, rolling, settled }) => (
  <div className={`${diceStyles.die} ${rolling ? diceStyles.dieRolling : ''} ${settled ? diceStyles.dieSettled : ''}`}>
    {(FACE_DOTS[value] ?? FACE_DOTS[1]).map(([x, y], i) => (
      <span key={i} className={`${diceStyles.pip} ${diceStyles.pipVisible}`} style={{ left: `${x}%`, top: `${y}%` }} />
    ))}
  </div>
);

// ── Constants ────────────────────────────────────────────────────────────────
const ROLL_ANIM_MS = 1200;

type Choice = 'low' | 'seven' | 'high';

const CHOICES: { id: Choice; label: string; range: string; payout: string }[] = [
  { id: 'low',   label: 'LOW',   range: '2 – 6',  payout: 'x1.4' },
  { id: 'seven', label: 'SEVEN', range: '= 7',    payout: 'x4' },
  { id: 'high',  label: 'HIGH',  range: '8 – 12', payout: 'x1.4' },
];

// ── Component ────────────────────────────────────────────────────────────────
const HighLow: React.FC<GamblingGameProps> = ({ mercenaryId, silver, remaining, nextRollAt, onResult }) => {
  const [bet, setBet] = useState(10);
  const [choice, setChoice] = useState<Choice | null>(null);
  const [rolling, setRolling] = useState(false);
  const [diceValues, setDiceValues] = useState<[number, number]>([1, 1]);
  const [result, setResult] = useState<{ win: boolean; message: string } | null>(null);
  const [cooldownDisplay, setCooldownDisplay] = useState<string | null>(null);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shuffleRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cooldown ticker
  useEffect(() => {
    if (!nextRollAt) { setCooldownDisplay(null); return; }
    const tick = () => {
      const diff = nextRollAt.getTime() - Date.now();
      if (diff <= 0) { setCooldownDisplay(null); return; }
      const m = Math.floor(diff / 60000);
      const s = Math.ceil((diff % 60000) / 1000);
      setCooldownDisplay(m > 0 ? `${m}m ${s}s` : `${s}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [nextRollAt]);

  const scheduleResultDismiss = useCallback(() => {
    if (dismissTimer.current) clearTimeout(dismissTimer.current);
    dismissTimer.current = setTimeout(() => setResult(null), 5000);
  }, []);

  const handlePlay = async () => {
    if (!choice || rolling || cooldownDisplay || remaining <= 0) return;
    setRolling(true);
    setResult(null);

    // Shuffle animation
    shuffleRef.current = setInterval(() => {
      setDiceValues([Math.ceil(Math.random() * 6) as 1, Math.ceil(Math.random() * 6) as 1]);
    }, 80);

    try {
      const res = await axios.post('/api/tavern/play-highlow', { mercenaryId, bet, choice });
      setTimeout(() => {
        if (shuffleRef.current) { clearInterval(shuffleRef.current); shuffleRef.current = null; }
        setDiceValues([res.data.roll1, res.data.roll2]);
        setRolling(false);
        setResult({ win: res.data.win, message: res.data.message });
        onResult(res.data.newSilver, res.data.remaining, res.data.nextRollAt ? new Date(res.data.nextRollAt) : null);
        scheduleResultDismiss();
      }, ROLL_ANIM_MS);
    } catch (err: any) {
      if (shuffleRef.current) { clearInterval(shuffleRef.current); shuffleRef.current = null; }
      setRolling(false);
      setResult({ win: false, message: err?.response?.data?.message || err?.response?.data?.error || 'Error placing bet.' });
      scheduleResultDismiss();
    }
  };

  const canRoll = !rolling && !cooldownDisplay && remaining > 0 && choice !== null && bet >= 1;
  const sum = diceValues[0] + diceValues[1];

  return (
    <div className={styles.game}>
      <p className={diceStyles.description}>
        Predict whether two dice will land Low (2–6) or High (8–12) to win x1.4, or call exactly Seven to win x4.
      </p>

      {/* Choice buttons */}
      <div className={styles.choiceRow}>
        {CHOICES.map(c => (
          <button
            key={c.id}
            className={`${styles.choiceBtn} ${styles[c.id]} ${choice === c.id ? styles.chosen : ''}`}
            onClick={() => setChoice(c.id)}
          >
            <span className={styles.choiceLabel}>{c.label}</span>
            <span className={styles.choiceRange}>{c.range}</span>
            <span className={styles.choicePayout}>Pays {c.payout}</span>
          </button>
        ))}
      </div>

      {/* Dice */}
      <div className={diceStyles.diceArea}>
        <DieFace value={diceValues[0]} rolling={rolling} settled={!rolling && result !== null} />
        <DieFace value={diceValues[1]} rolling={rolling} settled={!rolling && result !== null} />
        <span className={styles.sumDisplay}>{!rolling ? `= ${sum}` : '?'}</span>
      </div>

      {/* Bet & roll controls */}
      <div className={diceStyles.betControls}>
        <label>Bet:</label>
        <input
          type="number"
          className={diceStyles.plainInput}
          value={bet || ''}
          placeholder="1"
          min={1}
          max={Math.min(silver, remaining)}
          onChange={e => { const raw = e.target.value; setBet(raw === '' ? 0 : Math.min(silver, remaining, parseInt(raw) || 0)); }}
        />
        <button className={diceStyles.rollButton} onClick={handlePlay} disabled={!canRoll}>
          {rolling         ? 'Rolling…'
            : cooldownDisplay ? `Wait ${cooldownDisplay}`
            : remaining <= 0  ? 'Limit reached'
            : !choice         ? 'Pick a side'
            : 'Roll!'}
        </button>
      </div>

      {/* Result */}
      {result && (
        <div className={result.win ? diceStyles.resultSuccess : diceStyles.resultError}>
          {result.message}
        </div>
      )}
    </div>
  );
};

export default HighLow;
